import {
    AnnotationConfig,
    AnnotationState,
    SvgAnnotationsEditorConfig,
} from 'shared/svg_annotations/types';
import { clamp } from 'utils/number';

export const NewAnnotationState: AnnotationState = { type: 'new' };
export const ReadonlyAnnotationState: AnnotationState = { type: 'readonly' };

export const StrokeWidth = 1.75;
export const DragPointClickRadius = 10;
export const DragPointRadius = 3;
export const SelectionStrokeWidth = 10;
export const PinTextFontSize = 12;
export const PinTextFontFamily = 'REM';
export const StrokeDasharray = `4 4`;
export const PinLineLength = 35;
export const PinSize = 10;

export const ArrowWidth = 8;
export const ArrowHeight = 7;

export const ArrowPoints = [
    `0 ${ArrowHeight / 2}`,
    `${ArrowWidth} 0`,
    `${ArrowWidth} ${ArrowHeight}`,
].join(', ');

export const getAnnotationState = (
    config: AnnotationConfig,
    editorConfig?: SvgAnnotationsEditorConfig,
): AnnotationState => {
    if (!editorConfig) {
        return ReadonlyAnnotationState;
    }
    if (editorConfig.selectedAnnotationId === config.id) {
        return {
            type: 'selected',
            onUpdate: (newConfig) => editorConfig.onUpdate(newConfig, true),
        };
    }
    return { type: 'selectable', onSelect: () => editorConfig.onSelect(config) };
};

export const getSvgCursor = (
    editorConfig?: SvgAnnotationsEditorConfig,
): React.CSSProperties['cursor'] => {
    if (!editorConfig) {
        return undefined;
    }
    if (editorConfig.selectedAnnotationId) {
        return 'default';
    }
    return 'crosshair';
};

export const addStrokeAnimation = (element: SVGElement | null) => {
    if (element) {
        element.style.strokeDasharray = StrokeDasharray;

        element.animate([{ strokeDashoffset: 0 }, { strokeDashoffset: 8 }], {
            duration: 500,
            iterations: Infinity,
        });
    }
};

export const removeStrokeAnimation = (element: SVGElement | null) => {
    if (element) {
        element.style.strokeDasharray = '0';
        element.animate([]);
    }
};

export const degreesToRadians = (degrees: number) => degrees * (Math.PI / 180);

export const radiansToDegrees = (radians: number) => radians * (180 / Math.PI);

export const getAngleInDegrees = (x1: number, y1: number, x2: number, y2: number) =>
    radiansToDegrees(Math.atan2(-(y2 - y1), x2 - x1));

export const polarToPixel = (degrees: number, radius: number) => {
    const radians = degreesToRadians(degrees);

    return {
        x: Math.cos(radians) * radius,
        y: Math.sin(-radians) * radius,
    };
};

export const percentToViewBoxConfig = (
    config: AnnotationConfig,
    viewBoxWidth: number,
    viewBoxHeight: number,
): AnnotationConfig => {
    const vbx = (px: number) => (px / 100) * viewBoxWidth;
    const vby = (py: number) => (py / 100) * viewBoxHeight;

    switch (config.type) {
        case 'arrow':
            return {
                ...config,
                headX: vbx(config.headX),
                headY: vby(config.headY),
                tailX: vbx(config.tailX),
                tailY: vby(config.tailY),
            };
        case 'rectangle':
        case 'ellipse':
            return {
                ...config,
                x1: vbx(config.x1),
                y1: vby(config.y1),
                x2: vbx(config.x2),
                y2: vby(config.y2),
            };
        case 'text':
            return { ...config, x: vbx(config.x), y: vby(config.y) };
    }
};

export const viewBoxToPercentConfig = (
    config: AnnotationConfig,
    viewBoxWidth: number,
    viewBoxHeight: number,
): AnnotationConfig => {
    const px = (vbx: number) => (vbx / viewBoxWidth) * 100;
    const py = (vby: number) => (vby / viewBoxHeight) * 100;

    switch (config.type) {
        case 'arrow':
            return {
                ...config,
                headX: px(config.headX),
                headY: py(config.headY),
                tailX: px(config.tailX),
                tailY: py(config.tailY),
            };
        case 'rectangle':
        case 'ellipse':
            return {
                ...config,
                x1: px(config.x1),
                y1: py(config.y1),
                x2: px(config.x2),
                y2: py(config.y2),
            };
        case 'text':
            return { ...config, x: px(config.x), y: py(config.y) };
    }
};

export type Rectangle = Readonly<{ x1: number; y1: number; x2: number; y2: number }>;

const fitAxis = (a: number, b: number, max: number): [number, number] => {
    const lo = Math.min(a, b);
    const size = Math.abs(b - a);
    if (size >= max) {
        return [0, max];
    }
    const start = clamp(lo, 0, max - size);
    return [start, start + size];
};

/**
 * Fits a drag-defined rectangle into a [0, width] x [0, height] coordinate space.
 *
 * Preserves the rectangle's size by shifting it back into bounds when
 * possible; clamps any axis where the rectangle is too large to fit.
 * Output is always normalized so x1 <= x2 and y1 <= y2.
 */
export const normalizeDragMoveArea = (
    rect: Rectangle,
    { width, height }: { width: number; height: number },
): Rectangle => {
    const [x1, x2] = fitAxis(rect.x1, rect.x2, width);
    const [y1, y2] = fitAxis(rect.y1, rect.y2, height);
    return { x1, y1, x2, y2 };
};
